import { useContext } from "react";
import { BrandContext, AuthContext } from "../contexts";
import { Btn } from "../components/ui";
import HeroShader from "../components/HeroShader";

const LandingView = ({ setView, setInitialAuthMode }) => {
  const b = useContext(BrandContext);
  const { user } = useContext(AuthContext);
  return (
  <div>

    {/* ── Hero with WebGL background ── */}
    <div style={{ position: "relative", minHeight: "85vh", display: "flex", alignItems: "center", overflow: "hidden" }}>
      <HeroShader />

      {/* Gradient overlay for text readability */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 1,
        background: "linear-gradient(180deg, rgba(17,17,16,0.3) 0%, rgba(17,17,16,0.1) 40%, rgba(17,17,16,0.7) 85%, rgba(17,17,16,1) 100%)",
      }} />

      <div className="fade-up" style={{ position: "relative", zIndex: 2, maxWidth: 1100, margin: "0 auto", padding: "120px 24px 80px", width: "100%" }}>
        <p style={{
          fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.22em",
          color: "var(--amber)", textTransform: "uppercase", marginBottom: 20,
          display: "inline-block", padding: "6px 14px",
          background: "rgba(0, 212, 170, 0.08)", border: "1px solid rgba(0, 212, 170, 0.2)",
          borderRadius: "20px",
        }}>
          {b.heroEyebrow}
        </p>
        <h1 style={{
          fontSize: "clamp(48px, 8vw, 88px)", lineHeight: 0.95, letterSpacing: "-0.03em",
          marginBottom: 28, maxWidth: 780,
        }}>
          {b.tagline.replace(b.taglineEm, "").trimEnd()}<br />
          <em style={{ color: "var(--amber)" }}>{b.taglineEm}</em>
        </h1>
        <p style={{
          color: "var(--text-dim)", maxWidth: 520, fontSize: "18px", lineHeight: 1.7,
          marginBottom: 44,
        }}>
          {b.heroDescription}
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          {user ? (
            <>
              <Btn onClick={() => setView("shop")}>Shop Now →</Btn>
              <a href="https://t.me/LimitlessDirectBot" target="_blank" rel="noopener noreferrer">
                <Btn variant="ghost">Follow Us on Telegram</Btn>
              </a>
            </>
          ) : (
            <>
              <Btn onClick={() => { setInitialAuthMode("signin"); setView("auth"); }}>Sign In to Shop →</Btn>
              <Btn variant="ghost" onClick={() => { setInitialAuthMode("signup"); setView("auth"); }}>Create Account</Btn>
            </>
          )}
        </div>
      </div>
    </div>

    {/* ── Features strip ── */}
    <div style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <div className="fade-up-2 r-grid" style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 40 }}>
        {[
          { label: "Purity Verified", desc: "Every batch independently tested with certificate of analysis" },
          { label: "Fast Shipping", desc: "Orders ship within 2 business days via USPS Priority" },
          { label: "For Research Only", desc: "High-purity compounds for qualified in-vitro researchers" },
        ].map((f, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <p style={{
              fontFamily: "var(--font-mono)", fontSize: "12px", letterSpacing: "0.12em",
              color: "var(--amber)", textTransform: "uppercase", marginBottom: 8, fontWeight: 500,
            }}>{f.label}</p>
            <p style={{ color: "var(--text-dim)", fontSize: "14px", lineHeight: 1.6 }}>{f.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* ── About / Mission ── */}
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
      <div className="fade-up-2 r-grid r-gap" style={{ padding: "72px 0", borderBottom: "1px solid var(--border)", display: "grid", gridTemplateColumns: "1fr 2fr", gap: 56 }}>
        <div>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: "var(--amber)", textTransform: "uppercase", marginBottom: 12 }}>Our approach</p>
          <h2 style={{ fontSize: "32px", lineHeight: 1.12, letterSpacing: "-0.02em" }}>
            {b.aboutHeading.split("\n").map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
          </h2>
        </div>
        <div>
          <p style={{ color: "var(--text-dim)", fontSize: "16px", lineHeight: 1.8, marginBottom: 20 }}>
            {b.aboutBody1}
          </p>
          <p style={{ color: "var(--text-dim)", fontSize: "16px", lineHeight: 1.8 }}>
            {b.aboutBody2}
          </p>
        </div>
      </div>

      {/* ── Disclaimer ── */}
      <div style={{ padding: "48px 0", borderBottom: "1px solid var(--border)", textAlign: "center" }}>
        <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: "var(--amber)", textTransform: "uppercase", marginBottom: 12 }}>Important Notice</p>
        <p style={{ color: "var(--text-dim)", fontSize: "14px", lineHeight: 1.7, maxWidth: 600, margin: "0 auto" }}>
          All products sold by Limitless Peptides are intended for laboratory and research purposes only. They are not intended for human consumption, veterinary use, or any therapeutic application. By purchasing, you confirm that you are a qualified researcher and agree to use these products solely for in-vitro research.
        </p>
      </div>

      {/* ── CTA footer ── */}
      <div className="fade-up-3" style={{ padding: "72px 0 80px" }}>
        <div style={{
          textAlign: "center", padding: "56px 32px",
          background: "linear-gradient(135deg, rgba(0,212,170,0.06) 0%, rgba(0,168,136,0.03) 100%)",
          border: "1px solid rgba(0, 212, 170, 0.15)",
          borderRadius: "8px",
        }}>
          <h3 style={{ fontSize: "30px", marginBottom: 12, letterSpacing: "-0.02em" }}>
            {user ? "Browse the full catalog" : "Ready to browse the full catalog?"}
          </h3>
          <p style={{ color: "var(--text-dim)", fontSize: "15px", marginBottom: 28, maxWidth: 400, margin: "0 auto 28px" }}>
            {user ? "Check out what's new in the shop." : "Create a free account or sign in to access the shop."}
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            {user ? (
              <Btn onClick={() => setView("shop")}>Shop Now →</Btn>
            ) : (
              <>
                <Btn onClick={() => { setInitialAuthMode("signin"); setView("auth"); }}>Sign In to Shop →</Btn>
                <Btn variant="ghost" onClick={() => { setInitialAuthMode("signup"); setView("auth"); }}>Create Account</Btn>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default LandingView;
